import type { ComputedRef, Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { ResultItem } from '@/utils/file';
import type {
  InitialData as PostCodeSearchSearchFormInitialData,
  ValidData as PostCodeSearchSearchFormValidData,
} from '@/components/searchForms/PostCodeSearchSearchForm.vue';
import type { DataItem as PostCodeSearchTableDataItem } from '@/components/tables/PostCodeSearchTable.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { format } from 'date-fns';
import _isEqual from 'lodash/isEqual';

import * as postCodeSearchTableHelpers from '@/helpers/tables/postCodeSearch';
import * as routeNames from '@/constants/routeNames';

export interface PostCodeSearchViewComposable {
  search: (formData: PostCodeSearchSearchFormValidData) => void;
  initialData: ComputedRef<PostCodeSearchSearchFormInitialData>;
  total: Readonly<Ref<number>>;
  results: Readonly<Ref<ReadonlyDeep<PostCodeSearchTableDataItem[]>>>;
  resultsForCsv: Readonly<Ref<ResultItem[]>>;
}

export default function usePostCodeSearchView(
  querySuburbId: Readonly<Ref<number | undefined>>,
  queryPostCode: Readonly<Ref<number | undefined>>,
  queryDistance: Readonly<Ref<number | undefined>>,
  queryDates: Readonly<Ref<[Date, Date] | undefined>>,
  isStaff: Readonly<Ref<boolean>>,
  isFetchingPostCodeJobs: Readonly<Ref<boolean>>,
  postCodeJobs: Readonly<
    Ref<ReadonlyDeep<openapi.JobSearchJobByPostcodeContentOpenapi> | undefined>
  >,
): PostCodeSearchViewComposable {
  const router = useRouter();
  const route = useRoute();

  const initialData = computed<PostCodeSearchSearchFormInitialData>(() => {
    let postCodeItem: openapi.SuburbOpenapi | undefined;
    if (querySuburbId.value?.toString() && queryPostCode.value?.toString()) {
      postCodeItem = {
        id: querySuburbId.value,
        name: null,
        state: null,
        zone: '',
        status: 0,
        // TODO
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        postcode: queryPostCode.value?.toString(),
      };
    }
    return {
      postCodeItem,
      distance: queryDistance.value,
      dates: queryDates.value,
    };
  });

  const results = ref<PostCodeSearchTableDataItem[]>([]);

  const total = computed(() => {
    return results.value.length;
  });

  const resultsForCsv = computed<ResultItem[]>(() => {
    return results.value;
  });

  function search(formData: PostCodeSearchSearchFormValidData): void {
    const dates = [
      format(formData.dates[0], 'yyyy-MM-dd'),
      format(formData.dates[1], 'yyyy-MM-dd'),
    ].join(',');
    const query = {
      ...route.query,
      suburb_id: formData.postCodeItem.id?.toString(),
      postcode: formData.postCodeItem.postcode,
      distance: formData.distance.toString(),
      dates,
    };
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.SEARCH_POSTCODE,
      query,
      force: true,
      replace: isReplace,
    });
  }

  function updateResults(): void {
    if (!postCodeJobs.value) {
      results.value = [];
      return;
    }
    results.value = postCodeJobs.value.map((job) => {
      return postCodeSearchTableHelpers.getTableItem(job, isStaff.value);
    });
  }

  watch(isFetchingPostCodeJobs, function (newValues, oldValues) {
    if (route.name !== routeNames.SEARCH_POSTCODE) return;
    if (isFetchingPostCodeJobs.value) return;
    updateResults();
  });

  watch(
    [querySuburbId, queryPostCode, queryDistance, queryDates, route],
    async function (newValues, oldValues) {
      if (route.name !== routeNames.SEARCH_POSTCODE) return;
      if (isFetchingPostCodeJobs.value) return;
      updateResults();
    },
    { immediate: true },
  );

  return {
    search,
    initialData,
    total,
    results,
    resultsForCsv,
  };
}
